import React, { useState, useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import _ from 'lodash';
import moment from 'moment';
import { Pie, Bar } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import { Card, Button, Segmented, Table, Tooltip, Spin, Divider } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { percent, randomColor, renderShortDate, renderShortDateTime, thousand, thousandv2, toCurrency } from '../../../utils/functions';
import { dashboardPayloadAtom, dashboardSelectedStoreAtom, timezoneAtom } from '../../../../atoms/Atoms';
import api from '../../../../api/api';
import { configSelector } from '../../../../atoms/Selectors';

function DashboardStatistics() {
  const isMobile = useMediaQuery({ query: '(max-width: 760px)' });
  const config = useRecoilValueLoadable(configSelector);
  const topRowGridStyle = { width: '100%', textAlign: 'center' };
  const bottomRowGridStyle = { width: '50%', textAlign: 'center' };
  const { t, i18n } = useTranslation();
  const selectedStoreId = useRecoilValue(dashboardSelectedStoreAtom);
  const [dashboardPayload, setDashboardPayload] =
    useRecoilState(dashboardPayloadAtom);
  const timezone = useRecoilValue(timezoneAtom);
  const [sales, setSales] = useState(null);
  const [loadingSales, setLoadingSales] = useState(true);

  const [tenders, setTenders] = useState({
    tenderTypes: [],
    cardTypes: []
  });
  const [loadingTenders, setLoadingTenders] = useState(true);
  const [selectedTenderOption, setSelectedTenderOption] = useState(t('tender_types'));

  const [divisions, setDivisions] = useState({
    divisions: [],
    itemGroups: [],
    departments: [],
    categories: [],
  });
  const [loadingDivisions, setLoadingDivisions] = useState(true);
  const [selectedDivisionOption, setSelectedDivisionOption] = useState(t('division'));

  const [timesheets, setTimesheets] = useState({
    default: [],
    grouped: [],
    individual: []
  });
  const [loadingTimesheets, setLoadingTimesheets] = useState(true);
  const [selectedTimesheetOption, setSelectedTimesheetOption] = useState(t('default'));

  function getSales() {
    setLoadingSales(true);
    let url = 'analytics/v2/stats/sales';
    if (selectedStoreId && selectedStoreId?.length > 0) {
      url += `?businessId=${selectedStoreId}`;
    }
    api
      .post(url, {
        Lifetime: dashboardPayload.isLifetime,
        StartDate: moment(dashboardPayload.start).utc(),
        EndDate: moment(dashboardPayload.end).utc().add(1, 'days'),
      })
      .then((response) => {
        setLoadingSales(false);
        setSales(response.data.data);
      })
      .catch((error) => {
        console.error(error);
        setLoadingSales(false);
      });
  }

  function getTenders() {
    setLoadingTenders(true);
    let url = 'analytics/v2/stats/tenders';
    if (selectedStoreId && selectedStoreId?.length > 0) {
      url += `?businessId=${selectedStoreId}`;
    }
    api
      .post(url, {
        Lifetime: dashboardPayload.isLifetime,
        StartDate: moment(dashboardPayload.start).utc(),
        EndDate: moment(dashboardPayload.end).utc().add(1, 'days'),
      })
      .then((response) => {
        setLoadingTenders(false);
        setTenders(response.data.data);
      })
      .catch((error) => {
        console.error(error);
        setLoadingTenders(false);
      });
  }

  function getDivisions() {
    setLoadingDivisions(true);
    let url = 'analytics/v2/stats/divisions';
    if (selectedStoreId && selectedStoreId?.length > 0) {
      url += `?businessId=${selectedStoreId}`;
    }
    api
      .post(url, {
        Lifetime: dashboardPayload.isLifetime,
        StartDate: moment(dashboardPayload.start).utc(),
        EndDate: moment(dashboardPayload.end).utc().add(1, 'days'),
      })
      .then((response) => {
        setLoadingDivisions(false);
        setDivisions(response.data.data);
      })
      .catch((error) => {
        console.error(error);
        setLoadingDivisions(false);
      });
  }

  function getTimesheets() {
    setLoadingTimesheets(true);
    let url = 'analytics/v2/stats/timesheets';
    if (selectedStoreId && selectedStoreId?.length > 0) {
      url += `?businessId=${selectedStoreId}`;
    }
    api
      .post(url, {
        Lifetime: dashboardPayload.isLifetime,
        StartDate: moment(dashboardPayload.start).utc(),
        EndDate: moment(dashboardPayload.end).utc().add(1, 'days'),
      })
      .then((response) => {
        setLoadingTimesheets(false);
        const temp = _.map(response.data.data.timeSheetDates, (ts) => ({
          ...ts,
          id: `${ts.user.id}-${ts.date}`,
          userId: ts.user.id
        }));
        const individual = [];
        for (let i = 0; i < temp?.length; i++) {
          for (let j = 0; j < temp[i].timesheet?.length; j++) {
            individual.push({
              date: temp[i].date,
              businessId: temp[i].businessId,
              businessName: temp[i].businessName,
              totalHours: temp[i].totalHours,
              totalMinutes: temp[i].totalMinutes,
              user: temp[i].user,
              id: temp[i].timesheet[j].id,
              startTime: temp[i].timesheet[j].startTime,
              endTime: temp[i].timesheet[j].endTime,
              hours: temp[i].timesheet[j].hours,
              minutes: temp[i].timesheet[j].minutes,
            });
          }
        }
        const tempGrouped = _.groupBy(temp, (sheet) => sheet.userId);
        const grouped = _.map(tempGrouped, (userSheets) => {
          const tempSheet = {
            ...userSheets[0],
            timesheet: []
          };
          for (let i = 0; i < userSheets?.length; i++) {
            if (i !== 0) {
              tempSheet.totalHours += userSheets[i].totalHours;
              tempSheet.totalMinutes += userSheets[i].totalMinutes;
            }
            for (let j = 0; j < userSheets[i].timesheet?.length; j++) {
              tempSheet.timesheet.push(userSheets[i].timesheet[j]);
            }
          }
          return tempSheet;
        });
        setTimesheets({
          default: temp,
          grouped,
          individual
        });
      })
      .catch((error) => {
        console.error(error);
        setLoadingTimesheets(false);
      });
  }

  function getData() {
    getSales();
    getTenders();
    getDivisions();
    getTimesheets();
  }

  function getRelevantTendersList() {
    return selectedTenderOption === t('tender_types') ? tenders?.tenderTypes : tenders?.cardTypes;
  }

  function shouldShowTendersList() {
    return tenders?.tenderTypes.length > 0 || tenders?.cardTypes.length > 0;
  }

  function getRelevantDivisionList() {
    if (selectedDivisionOption === t('division')) {
      return divisions?.divisions;
    }
    if (selectedDivisionOption === t('product_group')) {
      return divisions?.itemGroups;
    }
    if (selectedDivisionOption === t('department')) {
      return divisions?.departments;
    }
    return divisions?.categories;
  }

  function shouldShowDivisionsList() {
    return divisions?.divisions.length > 0 || divisions?.itemGroups.length > 0 ||
      divisions?.departments.length > 0 || divisions?.categories.length > 0;
  }

  function getBarChartOptions() {
    if (isMobile) {
      return {
        indexAxis: 'y',
        zoomMode: 'y'
      };
    }
    return {
      indexAxis: 'x',
      zoomMode: 'x'
    };
  }

  useEffect(() => {
    getData();
  }, [dashboardPayload.isLifetime, selectedStoreId]);

  useEffect(() => {
    if (dashboardPayload.load) {
      getData();
    }
  }, [dashboardPayload.load]);

  return (
    <div className="mb-12">
      <div className="flex">
        <div className="ml-auto flex mb-2">
          <Button
            type="primary"
            size="middle"
            icon={<ReloadOutlined />}
            className="ml-2"
            onClick={() => getData()}
          >
            {t('reload')}
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        <p className="col-span-1 md:col-span-2 lg:col-span-3 font-bold text-2xl">{t('sales')}</p>
        <Card
          type="inner"
          title={t('orders')}
          className="col-span-1 md:col-span-2 lg:col-span-1 rounded-xl bg-gradient-to-br from-blue-200 to-white"
          hoverable
          loading={loadingSales}
        >
          <Card.Grid
            className="bg-white"
            hoverable={false}
            style={topRowGridStyle}
          >
            <span>{thousand(sales?.ordersTotal)}</span>
          </Card.Grid>
        </Card>
        <Card
          type="inner"
          title={t('sales')}
          className="col-span-1 rounded-xl bg-gradient-to-br from-blue-200 to-white"
          hoverable
          loading={loadingSales}
        >
          <Card.Grid
            className="bg-white"
            hoverable={false}
            style={topRowGridStyle}
          >
            <span> {toCurrency(sales?.salesTotal)}</span>
          </Card.Grid>
        </Card>
        <Card
          type="inner"
          title={t('average')}
          className="col-span-1 rounded-xl bg-gradient-to-br from-blue-200 to-white"
          hoverable
          loading={loadingSales}
        >
          <Card.Grid
            className="bg-white"
            hoverable={false}
            style={topRowGridStyle}
          >
            <span> {toCurrency(sales?.saleAverage)}</span>
          </Card.Grid>
        </Card>

        <Divider className={`col-span-1 md:col-span-2 lg:col-span-3 ${!loadingTenders && shouldShowTendersList() ? '' : 'hidden'}`} />
        <div className="col-span-1 md:col-span-2 lg:col-span-3 grid grid-col-span-1 md:grid-col-span-2 divide-y">
          <Spin spinning={loadingTenders}>
            <div className={!loadingTenders && shouldShowTendersList() ? '' : 'hidden'}>
              <p className="font-bold text-2xl">{t('payment')} {t('participation')}</p>
              <div className="grid grod-cols-1 w-full">
                <Segmented
                  size="medium"
                  className="mt-2 col-span-1"
                  block
                  primary
                  options={[t('tender_types'), t('card_types')]}
                  value={selectedTenderOption}
                  onChange={(value) => {
                    setSelectedTenderOption(value);
                  }}
                />
                <div className="col-span-1 h-56">
                  <Bar
                    height={350}
                    data={{
                      labels: _.map(getRelevantTendersList(), (s) => (i18n.language === 'en' ? s.labelEng : s.labelSpa)),
                      datasets: [
                        {
                          label: t('participation'),
                          data: _.map(getRelevantTendersList(), (s) => s.value),
                          borderColor: config?.contents?.data?.primaryColor,
                          backgroundColor: _.map(getRelevantTendersList(), (x, i) => (i % 2 === 0 ?
                            config?.contents?.data?.primaryColor : config?.contents?.data?.secondaryColor)),
                          pointStyle: 'circle',
                          pointRadius: 6,
                          fill: true
                        },
                      ],
                    }}
                    options={{
                      indexAxis: getBarChartOptions().indexAxis,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          ticks: {
                            callback: (value, index, ticks) => (getBarChartOptions().indexAxis === 'x' ? percent(value) : (i18n.language === 'en' ? getRelevantTendersList()[index].labelEng : getRelevantTendersList()[index].labelSpa))
                          }
                        },
                        x: {
                          ticks: {
                            callback: (value, index, ticks) => (getBarChartOptions().indexAxis === 'y' ? percent(value) : (i18n.language === 'en' ? getRelevantTendersList()[index].labelEng : getRelevantTendersList()[index].labelSpa))
                          }
                        },
                      },
                      responsive: true,
                      plugins: {
                        zoom: {
                          pan: {
                            enabled: true,
                            mode: getBarChartOptions().zoomMode,
                          },
                          zoom: {
                            wheel: {
                              enabled: true,
                            },
                            pinch: {
                              enabled: true
                            },
                            mode: getBarChartOptions().zoomMode,
                          }
                        },
                        legend: {
                          position: 'top',
                        },
                        title: {
                          display: false,
                        },
                        datalabels: {
                          display: true,
                          offset: -30,
                          align: 'start',
                          color: 'white',
                          backgroundColor: 'black',
                          borderColor: 'black',
                          borderRadius: 5,
                          formatter: (value, context) => `${percent(value)}%`,
                          labels: {
                            title: {
                              font: {
                                weight: 'regular'
                              }
                            },
                          }
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </Spin>

          <Spin spinning={loadingDivisions}>
            <div className={!loadingDivisions && shouldShowDivisionsList() ? '' : 'hidden'}>
              <p className="mt-2 font-bold text-2xl">{t('grouping')} {t('participation')}</p>
              <div className="grid grod-cols-1 w-full">
                <Segmented
                  size="medium"
                  className="mt-2 col-span-1"
                  block
                  primary
                  options={[t('division'), t('product_group'), t('department'), t('category')]}
                  value={selectedDivisionOption}
                  onChange={(value) => {
                    setSelectedDivisionOption(value);
                  }}
                />
                <div className="col-span-1 h-56">
                  <Bar
                    height={350}
                    data={{
                      labels: _.map(getRelevantDivisionList(), (s) => (i18n.language === 'en' ? s.labelEng : s.labelSpa)),
                      datasets: [
                        {
                          label: t('participation'),
                          data: _.map(getRelevantDivisionList(), (s) => s.value),
                          borderColor: config?.contents?.data?.primaryColor,
                          backgroundColor: _.map(getRelevantDivisionList(), (x, i) => (i % 2 === 0 ?
                            config?.contents?.data?.primaryColor : config?.contents?.data?.secondaryColor)),
                          pointStyle: 'circle',
                          pointRadius: 6,
                          fill: true
                        },
                      ],
                    }}
                    options={{
                      indexAxis: getBarChartOptions().indexAxis,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          ticks: {
                            callback: (value, index, ticks) => (getBarChartOptions().indexAxis === 'x' ? percent(value) : (i18n.language === 'en' ? getRelevantDivisionList()[index].labelEng : getRelevantDivisionList()[index].labelSpa))
                          }
                        },
                        x: {
                          ticks: {
                            callback: (value, index, ticks) => (getBarChartOptions().indexAxis === 'y' ? percent(value) : (i18n.language === 'en' ? getRelevantDivisionList()[index].labelEng : getRelevantDivisionList()[index].labelSpa))
                          }
                        },
                      },
                      responsive: true,
                      plugins: {
                        zoom: {
                          pan: {
                            enabled: true,
                            mode: getBarChartOptions().zoomMode,
                          },
                          zoom: {
                            wheel: {
                              enabled: true,
                            },
                            pinch: {
                              enabled: true
                            },
                            mode: getBarChartOptions().zoomMode,
                          }
                        },
                        legend: {
                          position: 'top',
                        },
                        title: {
                          display: false,
                        },
                        datalabels: {
                          display: true,
                          offset: -30,
                          align: 'start',
                          color: 'white',
                          backgroundColor: 'black',
                          borderColor: 'black',
                          borderRadius: 5,
                          formatter: (value, context) => `${percent(value)}%`,
                          labels: {
                            title: {
                              font: {
                                weight: 'regular'
                              }
                            },
                          }
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </Spin>
        </div>

        <Divider className={`col-span-1 md:col-span-2 lg:col-span-3 ${!loadingTimesheets && timesheets?.default.length === 0 ? 'hidden' : ''}`} />
        <div className={`col-span-1 md:col-span-2 lg:col-span-3 ${!loadingTimesheets && timesheets?.default.length === 0 ? 'hidden' : ''}`}>
          <p className="mt-2 font-bold text-2xl">{t('timesheets')}</p>
          <Spin spinning={loadingTimesheets}>
            <Segmented
              size="medium"
              className="mt-2"
              block
              primary
              options={[t('default'), t('grouped'), t('individual')]}
              value={selectedTimesheetOption}
              onChange={(value) => {
                setSelectedTimesheetOption(value);
              }}
            />
          </Spin>
          <div>
            <Table
              pagination={false}
              size="small"
              className="mt-2"
              bordered
              rowKey="id"
              loading={loadingTimesheets}
              dataSource={selectedTimesheetOption === t('default') ? timesheets?.default : (selectedTimesheetOption === t('grouped') ? timesheets?.grouped : timesheets?.individual)}
              columns={[
                {
                  title: t('day'),
                  key: 'day',
                  align: 'center',
                  className: `text-xs ${selectedTimesheetOption === t('default') ? '' : 'hidden'}`,
                  render: (text) => <p>{renderShortDate(text.date, timezone)}</p>,
                },
                {
                  title: t('start_time'),
                  key: 'starttime',
                  align: 'center',
                  className: `text-xs ${selectedTimesheetOption === t('individual') ? '' : 'hidden'}`,
                  render: (text) => <p>{renderShortDateTime(text.startTime, timezone)}</p>,
                },
                {
                  title: t('end_time'),
                  key: 'endtime',
                  align: 'center',
                  className: `text-xs ${selectedTimesheetOption === t('individual') ? '' : 'hidden'}`,
                  render: (text) => (
                    <div>
                      {text.endTime && (<p>{renderShortDateTime(text.endTime, timezone)}</p>)}
                      {!text.endTime && <p>-</p>}
                    </div>
                  ),
                },
                {
                  title: t('first_name'),
                  key: 'firstName',
                  align: 'center',
                  className: 'text-xs',
                  render: (text) => <p>{text?.user?.firstName}</p>,
                },
                {
                  title: t('last_name'),
                  key: 'lastName',
                  align: 'center',
                  className: 'text-xs',
                  render: (text) => <p>{text?.user?.lastName}</p>,
                },
                {
                  title: t('hours'),
                  key: 'totalhours',
                  align: 'center',
                  className: `text-xs ${selectedTimesheetOption === t('individual') ? 'hidden' : ''}`,
                  render: (text) => (
                    <div>
                      <Tooltip title={`${thousand(text.totalMinutes)} ${t('minutes')}`}>
                        <FontAwesomeIcon icon={faClock} className="mr-1 text-xs" />
                        {thousandv2(text.totalHours)}
                      </Tooltip>
                    </div>
                  ),
                },
                {
                  title: t('hours'),
                  key: 'hours',
                  align: 'center',
                  className: `text-xs ${selectedTimesheetOption === t('individual') ? '' : 'hidden'}`,
                  render: (text) => (
                    <div>
                      {text.minutes && (
                        <Tooltip title={`${thousand(text.minutes)} ${t('minutes')}`}>
                          <FontAwesomeIcon icon={faClock} className="mr-1 text-xs" />
                          {thousandv2(text.hours)}
                        </Tooltip>
                      )}
                      {!text.minutes && (
                        <p>-</p>
                      )}
                    </div>
                  ),
                }
              ]}
              expandable={{
                expandRowByClick: false,
                rowExpandable: (record) => selectedTimesheetOption !== t('individual'),
                expandedRowRender: (record) => (
                  <Table
                    pagination={false}
                    size="small"
                    bordered
                    rowKey="id"
                    dataSource={record.timesheet}
                    columns={[
                      {
                        title: t('start_time'),
                        key: 'starttime',
                        align: 'center',
                        className: 'text-xs',
                        render: (text) => <p>{renderShortDateTime(text.startTime, timezone)}</p>,
                      },
                      {
                        title: t('end_time'),
                        key: 'endtime',
                        align: 'center',
                        className: 'text-xs',
                        render: (text) => (
                          <div>
                            {text.endTime && (<p>{renderShortDateTime(text.endTime, timezone)}</p>)}
                            {!text.endTime && <p>-</p>}
                          </div>
                        ),
                      },
                      {
                        title: t('hours'),
                        key: 'hours',
                        align: 'center',
                        className: 'text-xs',
                        render: (text) => (
                          <div>
                            {text.minutes && (
                              <Tooltip title={`${thousand(text.minutes)} ${t('minutes')}`}>
                                <FontAwesomeIcon icon={faClock} className="mr-1 text-xs" />
                                {thousandv2(text.hours)}
                              </Tooltip>
                            )}
                            {!text.minutes && (
                              <p>-</p>
                            )}
                          </div>
                        ),
                      }
                    ]}
                  />
                )
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardStatistics;
